import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { PHILOSOPHY } from "@/lib/assets";

const reveal = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

export const Philosophy = () => {
  const { t } = useLanguage();
  const pillars = t("philosophy.pillars");

  return (
    <section
      id="philosophy"
      data-testid="philosophy-section"
      className="relative py-28 sm:py-40 bg-[#0B1622]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header — asymmetric */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-7"
          >
            <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-8">
              {t("philosophy.eyebrow")}
            </p>
            <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
              {t("philosophy.title")}
              <span className="gold-gradient-text italic">{t("philosophy.titleAccent")}</span>
            </h2>
          </motion.div>
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-5 text-[#A0ABC0] text-base sm:text-lg font-light leading-relaxed"
            data-testid="philosophy-intro"
          >
            {t("philosophy.intro")}
          </motion.p>
        </div>

        {/* Ambiance image with overlaid motto */}
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="mt-16 relative overflow-hidden"
          data-testid="philosophy-ambiance"
        >
          <img
            src={PHILOSOPHY.ambiance}
            alt="L'Art de la Forteresse — ambiance signature"
            className="w-full h-[420px] sm:h-[560px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1420] via-[#0A1420]/30 to-transparent" />
          <div className="absolute inset-0 border border-[#D4AF37]/25" />
          <div className="absolute bottom-8 left-8 sm:bottom-12 sm:left-12 max-w-xl">
            <p className="font-serif-lux italic text-2xl sm:text-4xl text-white leading-tight">
              {t("philosophy.motto")}
            </p>
            <p className="mt-3 text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.25em]">
              {t("philosophy.mottoTranslation")}
            </p>
            <p className="mt-6 leading-relaxed" data-testid="philosophy-luxemburgensia">
              <span className="gold-gradient-text font-serif-lux italic text-lg sm:text-2xl">
                {t("philosophy.luxemburgensia")}
              </span>
              <span className="block mt-1 text-white/60 text-xs sm:text-sm tracking-[0.15em] uppercase">
                {t("philosophy.luxemburgensiaDesc")}
              </span>
            </p>
          </div>
        </motion.div>

        {/* Three pillars */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.12 }}
              className="group"
              data-testid={`philosophy-pillar-${i}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={PHILOSOPHY.pillars[i]}
                  alt={p.title}
                  className="w-full h-72 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 border border-[#D4AF37]/20" />
                <span className="absolute top-4 left-4 font-serif-lux text-sm text-[#D4AF37]/70">
                  0{i + 1}
                </span>
              </div>
              <h3 className="mt-6 font-serif-lux text-2xl text-white">{p.title}</h3>
              <p className="mt-3 text-[#A0ABC0] font-light leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
